import * as React from "react";

/** How long a lane stays lit after the last event of that direction. */
const LINGER_MS = 2000;

/**
 * Drives the transmission indicator from discrete A2A events. Each direction
 * (outgoing / incoming) lights up the moment it's triggered and stays lit until
 * {@link LINGER_MS} after the *last* trigger — i.e. "the duration of the
 * communication plus 2 seconds". Re-triggering re-arms the timer, so a burst of
 * events holds the lane on continuously.
 *
 * Pure timing: the caller maps SSE events to {@link triggerOut}/{@link triggerIn}.
 */
export function useTransmissionActivity(lingerMs: number = LINGER_MS) {
  const [outActive, setOutActive] = React.useState(false);
  const [inActive, setInActive] = React.useState(false);
  const outTimer = React.useRef<number | null>(null);
  const inTimer = React.useRef<number | null>(null);

  const triggerOut = React.useCallback(() => {
    setOutActive(true);
    if (outTimer.current != null) window.clearTimeout(outTimer.current);
    outTimer.current = window.setTimeout(() => setOutActive(false), lingerMs);
  }, [lingerMs]);

  const triggerIn = React.useCallback(() => {
    setInActive(true);
    if (inTimer.current != null) window.clearTimeout(inTimer.current);
    inTimer.current = window.setTimeout(() => setInActive(false), lingerMs);
  }, [lingerMs]);

  React.useEffect(
    () => () => {
      if (outTimer.current != null) window.clearTimeout(outTimer.current);
      if (inTimer.current != null) window.clearTimeout(inTimer.current);
    },
    [],
  );

  return { outActive, inActive, triggerOut, triggerIn };
}
